import {
  addToast,
  Button,
  Collapse,
  ColumnTable,
  DisplayInfo,
  Table,
} from '@octano/global-ui';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';
import { useActionKitImp } from '../../../../components/ozmoit/common/action-kit-imp';
import { TranslationsKeys } from '../../../../locales/translations';
import { downloadFromBlob } from '../../../../utils/blob';
import { exportDetailsAction } from '../../actions';
import { useWarningsSummaryDetailsLoader } from '../../loaders/StudentWarningsSummaryDetailsLoader';

type AcademicWarningGroupEvaluation = {
  evaluation: string;
  date: string;
  grade: string;
};

type AcademicWarningGroupCourse = {
  course: string;
  evaluations: AcademicWarningGroupEvaluation[];
};

const AcademicWarningGroup = () => {
  const { result, query } = useWarningsSummaryDetailsLoader();
  const { t } = useTranslation(TranslationsKeys.STUDENT_ALERTS_DETAILS);

  const { action: handleExport, loading: exporting } = useActionKitImp(
    exportDetailsAction,
    {
      onSuccess: () => {
        addToast({
          color: 'success',
          text: t(`group.academic.export.success`),
        });
      },
      onError: {
        unknown: () => {
          addToast({
            color: 'danger',
            text: t(`group.academic.export.failed`),
          });
        },
      },
    },
  );

  const items = useMemo(() => {
    if (!result?.success) {
      return [];
    }

    return (result.data?.groupData as AcademicWarningGroupCourse[]) ?? [];
  }, [result]);

  const columns = useMemo<ColumnTable<AcademicWarningGroupEvaluation>[]>(
    () => [
      {
        columnName: 'evaluation',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        headerText: t(`group.academic.table.evaluation`),
      },
      {
        columnName: 'date',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        headerText: t(`group.academic.table.date`),
        cellFormat: ({ value }) => {
          if (!value) return '';
          const date = new Date(value);
          return date.toLocaleDateString();
        },
      },
      {
        columnName: 'grade',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        headerText: t(`group.academic.table.grade`),
      },
    ],
    [t],
  );

  const handlePressExport = useCallback(async () => {
    if (exporting) {
      return;
    }
    const response = await handleExport(query);
    if (!response.success) {
      return;
    }

    const blob = new Blob([response.data], {
      type: 'vnd.ms-excel',
    });
    downloadFromBlob(blob, `${t(`group.academic.export.title`)}.xlsx`);
  }, [handleExport, query, exporting, t]);

  return (
    <div className="w-100 d-flex flex-column mt-3" style={{ gap: 12 }}>
      <Row>
        <Col xs={12}>
          <div className="d-flex justify-content-end">
            <Button
              text={t(`group.academic.actions.downloadReport`)}
              size="md"
              type="button"
              icon="download"
              loading={exporting}
              disabled={!items.length}
              outlined
              onClick={handlePressExport}
            />
          </div>
        </Col>
      </Row>
      {items.map((item, index) => (
        <Collapse
          key={index.toString()}
          headerClassName="bg-light rounded"
          renderTitle={() => (
            <span className="text-primary fw-700 fs-18">{item.course}</span>
          )}
        >
          <div
            style={{
              position: 'relative',
              transform: 'translateX(-1rem)',
              width: 'calc(100% + 2rem)',
            }}
            className="mt-2"
          >
            <Table data={item.evaluations} columns={columns} />
          </div>
        </Collapse>
      ))}
      {!items.length && (
        <DisplayInfo
          title={t(`group.academic.empty.title`)}
          textBody={t(`group.academic.empty.description`)}
        />
      )}
    </div>
  );
};

export default AcademicWarningGroup;
